import { useState } from 'react';
import { Caret, CheckboxButton, Dropdown, Input } from '../../shared';
import { translate } from '../../../lib/lang.helper';
import { useSelector } from 'react-redux';
import { selectLang } from '../../../store/ui/ui.selector';
import { SelectUtilsByFarmID } from '../../../store/extra/extra.selector';
import { ITimeRange, TBusinessType } from '../../../entities/general.entities';
import DirectionForm from '../../farm-util/DirectionForm';
import TimeTrackInput from '../../shared/input/TimeTrackInput';
import { getUnitOfSpecie, numToStr } from '../../../lib/common.helpers';
import SelectBoat from '../../farmform/SelectBoat';
import { selectAccount } from '../../../store/auth/auth.selector';

interface IForm {
  company?: string | null;
  boat_id?: number | null;
  tag_color?: string | null;
  port_of_unload?: string | null;
  crop_owner?: string | null;
  growing_area?: string | null;
  delivered_to?: string | null;
  packhouse?: string | null;
  shell_condition?: string | null;
  mussel_type?: string | null;
  mussels?: number | null;
  meat_yield?: number | null;
  blues?: number | null;
  marine_waste?: string | null;
  backbone_ok?: boolean;
  backbone_replace?: boolean;
  lights_ids_in_place?: boolean;
  flotation_on_farm?: boolean;
  rope_bags_quantity?: number | null;
  product_left_on_line?: string | null;
  oyster_type?: string;
  harvester_name?: string | null;
  direction?: string | null;
  time_tracks?: ITimeRange[];
}

const fieldNames = {
  MUSSEL: [
    'company',
    'tag_color',
    'port_of_unload',
    'crop_owner',
    'growing_area',
    'delivered_to',
    'packhouse',
    'shell_condition',
    'mussel_type',
    'mussels',
    'meat_yield',
    'blues',
    'marine_waste',
    'backbone_ok',
    'backbone_replace',
    'lights_ids_in_place',
    'flotation_on_farm',
    'rope_bags_quantity',
    'product_left_on_line',
    'harvester_name',
  ],
  OYSTER: [
    'port_of_unload',
    'crop_owner',
    'growing_area',
    'delivered_to',
    'packhouse',
    'shell_condition',
    'oyster_type',
    'harvester_name',
  ],
  SEAWEED: [],
};

interface Props {
  type: TBusinessType;
  farmID?: number;
  form: IForm;
  onChange: (d: any) => void;
  disabled?: boolean;
}

const MoreFieldsForm = ({
  type: cropType,
  farmID,
  form,
  onChange,
  disabled,
}: Props) => {
  const lang = useSelector(selectLang);
  const farmUtils = SelectUtilsByFarmID(farmID);
  const port_of_unloads = farmUtils.filter(x => x.type === 'port_of_unload'),
    delivered_tos = farmUtils.filter(x => x.type === 'delivered_to'),
    packhouses = farmUtils.filter(x => x.type === 'packhouse'),
    musselTypes = farmUtils.filter(x => x.type === 'harvest_type');
  const unitType = useSelector(selectAccount)?.display_unit;

  const [visible, setVisible] = useState(false);
  const [showPort, setShowPort] = useState(
    !port_of_unloads || port_of_unloads?.length <= 0,
  );
  const [showDel, setShowDel] = useState(
    !delivered_tos || delivered_tos?.length <= 0,
  );
  const [showPac, setShowPac] = useState(
    !packhouses || packhouses?.length <= 0,
  );

  const updateForm = (key: keyof IForm, value: any) => {
    if (key === 'mussels' || key === 'meat_yield') {
      value = value.length <= 0 ? null : Number(value);
    }
    onChange({ ...form, [key]: value });
  };
  const canShowField = (key: string) =>
    fieldNames[cropType].some(y => y === key);

  return (
    <div className='mb-17 mt-17 more-info-form'>
      <div
        className='d-flex justify-between cursor-pointer mb-17 pr-17'
        onClick={() => setVisible(!visible)}
      >
        <span style={{ fontSize: 16, fontWeight: 'bold' }}>
          {translate(lang, '_info')}
        </span>
        <Caret color='#5A607F' direction={visible ? 'top' : 'bottom'} />
      </div>
      {visible && (
        <>
          {canShowField('company') && (
            <div className='pb-10'>
              <Input
                type='text'
                onChange={e => updateForm('company', e.target.value)}
                value={form.company ?? ''}
                label={translate(lang, 'Company')}
                disabled={disabled}
              />
            </div>
          )}
          <SelectBoat
            className='mb-10'
            value={form.boat_id}
            onChange={v => updateForm('boat_id', v)}
            disabled={disabled}
          />
          {canShowField('tag_color') && (
            <div className='pb-10'>
              <Input
                label={translate(lang, 'Tag color')}
                type='text'
                onChange={e => updateForm('tag_color', e.target.value)}
                value={form.tag_color ?? ''}
                disabled={disabled}
              />
            </div>
          )}
          {canShowField('port_of_unload') && (
            <div className='pb-10'>
              <Dropdown
                label={translate(lang, 'Port of unload')}
                placeholder={translate(lang, '_sel_port_unload')}
                options={[
                  ...(port_of_unloads?.map(x => ({
                    id: x.id.toString(),
                    value: x.name,
                    label: x.name,
                  })) ?? []),
                  { id: '', value: '', label: translate(lang, 'Other') },
                ]}
                value={showPort ? '' : (form.port_of_unload ?? undefined)}
                onChange={v => {
                  updateForm('port_of_unload', v);
                  setShowPort(v.length <= 0);
                }}
              />
              {showPort && (
                <Input
                  label=''
                  placeholder={translate(lang, 'Port of unload')}
                  type='text'
                  className='mt-7'
                  onChange={e => updateForm('port_of_unload', e.target.value)}
                  value={form.port_of_unload ?? ''}
                  disabled={disabled}
                />
              )}
            </div>
          )}
          {canShowField('crop_owner') && (
            <div className='pb-10'>
              <Input
                label={translate(lang, 'Crop owner')}
                type='text'
                onChange={e => updateForm('crop_owner', e.target.value)}
                value={form.crop_owner ?? ''}
                disabled={disabled}
              />
            </div>
          )}
          {canShowField('growing_area') && (
            <div className='pb-10'>
              <Input
                label={translate(lang, 'Growing area')}
                type='text'
                onChange={e => updateForm('growing_area', e.target.value)}
                value={form.growing_area ?? ''}
                disabled={disabled}
              />
            </div>
          )}
          {canShowField('delivered_to') && (
            <div className='pb-10'>
              <Dropdown
                label={translate(lang, 'Delivered to')}
                placeholder={translate(lang, 'Select delivered to')}
                options={[
                  ...(delivered_tos?.map(x => ({
                    id: x.id.toString(),
                    value: x.name,
                    label: x.name,
                  })) ?? []),
                  { id: '', value: '', label: translate(lang, 'Other') },
                ]}
                value={showDel ? '' : (form.delivered_to ?? undefined)}
                onChange={v => {
                  updateForm('delivered_to', v);
                  setShowDel(v.length <= 0);
                }}
              />
              {showDel && (
                <Input
                  label=''
                  placeholder={translate(lang, 'Delivered to')}
                  type='text'
                  className='mt-7'
                  onChange={e => updateForm('delivered_to', e.target.value)}
                  value={form.delivered_to ?? ''}
                  disabled={disabled}
                />
              )}
            </div>
          )}
          {canShowField('packhouse') && (
            <div className='pb-10'>
              <Dropdown
                label={translate(lang, 'Packhouse')}
                placeholder={translate(lang, 'Select packhouse')}
                options={[
                  ...(packhouses?.map(x => ({
                    id: x.id.toString(),
                    value: x.name,
                    label: x.name,
                  })) ?? []),
                  { id: '', value: '', label: translate(lang, 'Other') },
                ]}
                value={showPac ? '' : (form.packhouse ?? undefined)}
                onChange={v => {
                  updateForm('packhouse', v);
                  setShowPac(v.length <= 0);
                }}
              />
              {showPac && (
                <Input
                  label=''
                  placeholder={translate(lang, 'Packhouse')}
                  type='text'
                  className='mt-7'
                  onChange={e => updateForm('packhouse', e.target.value)}
                  value={form.packhouse ?? ''}
                  disabled={disabled}
                />
              )}
            </div>
          )}
          {canShowField('shell_condition') && (
            <div className='pb-10'>
              <Input
                label={translate(lang, 'Shell condition')}
                type='text'
                onChange={e => updateForm('shell_condition', e.target.value)}
                value={form.shell_condition ?? ''}
                disabled={disabled}
              />
            </div>
          )}
          {canShowField('mussel_type') && (
            <div className='pb-10'>
              <Dropdown
                label={translate(lang, 'Mussels type')}
                options={
                  musselTypes?.map(x => ({
                    id: x.name,
                    label: x.name,
                    value: x.name,
                  })) ?? []
                }
                value={form.mussel_type ?? undefined}
                onChange={v => updateForm('mussel_type', v)}
                disabled={disabled}
              />
            </div>
          )}
          {canShowField('mussels') && (
            <div className='pb-10'>
              <Input
                label={translate(lang, 'Performance')}
                type='number'
                onChange={e => updateForm('mussels', e.target.value)}
                value={numToStr(form.mussels)}
                unit={`mussels per ${getUnitOfSpecie(unitType, 'weight_kg')}`}
                disabled={disabled}
              />
            </div>
          )}
          {canShowField('meat_yield') && (
            <div className='pb-10'>
              <Input
                label={translate(lang, 'Meat yield')}
                type='number'
                onChange={e => updateForm('meat_yield', e.target.value)}
                value={numToStr(form.meat_yield)}
                disabled={disabled}
                unit='%'
              />
            </div>
          )}
          {canShowField('blues') && (
            <div className='pb-10'>
              <Input
                label={translate(lang, '_blues_oysters')}
                type='number'
                onChange={e => updateForm('blues', e.target.value)}
                value={numToStr(form.blues)}
                unit='%'
                disabled={disabled}
              />
            </div>
          )}
          {canShowField('marine_waste') && (
            <div className='pb-10'>
              <Input
                label={translate(lang, '_marine_waste')}
                type='number'
                onChange={e => updateForm('marine_waste', e.target.value)}
                value={form.marine_waste ?? ''}
                disabled={disabled}
                unit={getUnitOfSpecie(unitType, 'weight_kg')}
              />
            </div>
          )}
          {canShowField('backbone_ok') && (
            <div className='pb-10'>
              <CheckboxButton
                label={translate(lang, 'Backbone OK')}
                onChange={e => updateForm('backbone_ok', e.target.checked)}
                checked={form.backbone_ok}
                disabled={disabled}
              />
            </div>
          )}
          {canShowField('backbone_replace') && (
            <div className='pb-10'>
              <CheckboxButton
                label={translate(lang, 'Backbone Replace')}
                onChange={e => updateForm('backbone_replace', e.target.checked)}
                checked={form.backbone_replace}
                disabled={disabled}
              />
            </div>
          )}
          {canShowField('lights_ids_in_place') && (
            <div className='pb-10'>
              <CheckboxButton
                label={translate(lang, '_lights_ids_place')}
                onChange={e =>
                  updateForm('lights_ids_in_place', e.target.checked)
                }
                checked={form.lights_ids_in_place}
                disabled={disabled}
              />
            </div>
          )}
          {canShowField('flotation_on_farm') && (
            <div className='pb-10'>
              <CheckboxButton
                label={translate(lang, 'Flotation on farm')}
                onChange={e =>
                  updateForm('flotation_on_farm', e.target.checked)
                }
                checked={form.flotation_on_farm}
                disabled={disabled}
              />
            </div>
          )}
          {canShowField('rope_bags_quantity') && (
            <div className='pb-10'>
              <Input
                label={translate(lang, 'Number of rope bags')}
                type='number'
                onChange={e => updateForm('rope_bags_quantity', e.target.value)}
                value={numToStr(form.rope_bags_quantity)}
                disabled={disabled}
              />
            </div>
          )}
          {canShowField('product_left_on_line') && (
            <div className='pb-10'>
              <Input
                label={translate(lang, 'Product left on line')}
                type='text'
                onChange={e =>
                  updateForm('product_left_on_line', e.target.value)
                }
                value={form.product_left_on_line ?? ''}
                disabled={disabled}
              />
            </div>
          )}
          {canShowField('oyster_type') && (
            <div className='pb-10'>
              <Input
                label={translate(lang, 'Oyster type')}
                type='text'
                onChange={e => updateForm('oyster_type', e.target.value)}
                value={form.oyster_type ?? ''}
                disabled={disabled}
              />
            </div>
          )}
          {canShowField('harvester_name') && (
            <div className='pb-10'>
              <Input
                label={translate(lang, "User's name")}
                type='text'
                onChange={e => updateForm('harvester_name', e.target.value)}
                value={form.harvester_name ?? ''}
                disabled={disabled}
              />
            </div>
          )}
          <div className='mb-17'>
            <DirectionForm
              value={form.direction}
              onChange={v => updateForm('direction', v)}
            />
          </div>
          {!!form.time_tracks && (
            <div className='pb-7'>
              <TimeTrackInput
                data={form.time_tracks}
                onChange={t => updateForm('time_tracks', t)}
                disabled={disabled}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MoreFieldsForm;
